/**
 * SubjectsPage — EntityTable framework (11E.4).
 *
 * Migrated from the CatalogPage tab. Category/Name inputs stay free-text
 * edits inside SubjectFormModal (reused as-is); "add new" category/name is
 * simply typing a new value — there is no fixed enum on the backend.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EntityTable } from '@/components/entity';
import { useTenantAwareEntityList } from '@/components/entity/useTenantAwareEntityList';
import { SubjectFormModal } from '@features/catalog/components/SubjectFormModal';
import {
  useUpdateSubject,
  getSubject,
  type SubjectResponse,
  type ListSubjectsPagedParams,
} from '@features/catalog/api/subjects';
import { subjectsTableConfig } from '@features/subjects/config/subjectsEntityConfig';
import { ImportExportButtons } from '@features/imports';
import type { EntityActionKey } from '@/components/entity/types';

const PAGE_SIZE = 20;

export const Subjects: React.FC = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(subjectsTableConfig.defaultSort ?? 'name');
  const [order, setOrder] = useState<'asc' | 'desc'>(subjectsTableConfig.defaultOrder ?? 'asc');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>(
    subjectsTableConfig.defaultFilters ?? {},
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editSubject, setEditSubject] = useState<SubjectResponse | undefined>();
  const [editLoading, setEditLoading] = useState(false);

  const queryParams: ListSubjectsPagedParams = {
    status: filters.status !== 'all' ? filters.status : undefined,
    search: search || undefined,
    sort,
    order,
    page,
    page_size: PAGE_SIZE,
  };

  const { data: result, isLoading, error, refetch, isAllMode } = useTenantAwareEntityList<SubjectResponse>({
    queryKey: ['subjects', 'list', queryParams],
    apiPath: subjectsTableConfig.apiPath,
    params: queryParams as unknown as Record<string, string | number | boolean | undefined | null>,
  });
  const updateMutation = useUpdateSubject();

  const handleSortChange = (newSort: string, newOrder: 'asc' | 'desc') => {
    setSort(newSort);
    setOrder(newOrder);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleRowClick = (subject: SubjectResponse) => {
    navigate(`/subjects/${subject.id}`);
  };

  const handleRowAction = async (action: EntityActionKey, subject: SubjectResponse) => {
    if (action === 'view') {
      navigate(`/subjects/${subject.id}`);
    } else if (action === 'edit') {
      setEditLoading(true);
      try {
        const fresh = await getSubject(subject.id);
        setEditSubject(fresh);
        setIsModalOpen(true);
      } catch {
        setEditSubject(subject);
        setIsModalOpen(true);
      } finally {
        setEditLoading(false);
      }
    } else if (action === 'archive') {
      if (!window.confirm(`Deactivate ${subject.name}?`)) return;
      await updateMutation.mutateAsync({ id: subject.id, body: { status: 'inactive' } });
    } else if (action === 'activate') {
      await updateMutation.mutateAsync({ id: subject.id, body: { status: 'active' } });
    }
  };

  const total = result?.total ?? 0;
  const subtitle = isLoading ? 'Loading…' : `${total.toLocaleString()} subjects`;

  return (
    <PageContainer
      title="Subjects"
      subtitle={subtitle}
      data-testid="candeza-subjects-page"
      data-flow="subjects-page"
      actions={
        <>
          <ImportExportButtons
            entity="subjects"
            entityLabel="Subjects"
            selectedIds={selectedIds}
            exportStatus={filters.status !== 'all' ? filters.status : undefined}
            exportSearch={search || undefined}
          />
          <Button
            onClick={() => { setEditSubject(undefined); setIsModalOpen(true); }}
            data-testid="candeza-subjects-add-btn"
            disabled={editLoading || isAllMode}
            title={isAllMode ? 'Select a specific school to add a subject' : undefined}
          >
            + Add subject
          </Button>
        </>
      }
    >
      <Card padding="lg">
        <EntityTable
          config={subjectsTableConfig}
          result={result}
          isLoading={isLoading}
          error={error as Error | null}
          sort={sort}
          order={order}
          search={search}
          filters={filters}
          page={page}
          selectedIds={selectedIds}
          onSortChange={handleSortChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onPageChange={setPage}
          onSelectChange={setSelectedIds}
          onRowClick={handleRowClick}
          onRowAction={handleRowAction}
          onRetry={() => refetch()}
          isAllMode={isAllMode}
        />
      </Card>

      <SubjectFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditSubject(undefined); }}
        subject={editSubject}
      />
    </PageContainer>
  );
};

